import { z } from 'zod';

import { AdminAccess } from './admin.js';
import { DeviceExtensions } from './device.js';
import { FeatureFlag, RETIRED_FEATURE_FLAGS } from './features.js';
import { Mdm } from './mdm.js';
import { Meta } from './meta.js';
import { TenantId, Uuid } from './primitives.js';
import { Profile, ProfileSchedule } from './profile.js';
import { RemoteConfig } from './remote.js';
import { Signage } from './signage.js';
import { TelemetryConfig } from './telemetry.js';

/**
 * Root MobiLauncher config — the single contract between Builder, runtime,
 * remote-config endpoint, and any future tooling.
 *
 * Cross-field rules (root-level):
 *   1. `defaultProfileId` must reference an entry in `profiles[]`.
 *   2. Every `schedules[].profileId` must reference an entry in `profiles[]`.
 *
 * Other refines live close to their sub-shapes:
 *   - `AdminAccess`: `exitPinHash` required when `exitGestureEnabled`
 *   - `Tile` (agentAction variant): `scriptRef` required when action='runScript'
 *   - `DeviceExtensions`: `force*` detection requires the matching sub-block
 *   - `Grid`: `tiles.length` must not exceed `columns × rows`
 */
export const MobiLauncherConfig = z
  .object({
    schemaVersion: z.literal('1.0'),
    tenantId: TenantId,
    deploymentName: z.string().min(1).max(120),
    /**
     * Which MDM platform owns this deployment. Drives Device Info
     * dialog field set and (later) native agent action routing.
     * Defaults to 'none' so existing configs without the field keep
     * the no-MDM behavior on parse.
     */
    mdm: Mdm.default('none'),
    defaultProfileId: Uuid,
    profiles: z.array(Profile).min(1).max(20),
    schedules: z.array(ProfileSchedule).default([]),
    adminAccess: AdminAccess,
    /**
     * Retired flags (see `RETIRED_FEATURE_FLAGS`) are stripped before enum
     * validation so configs published while a now-removed flag was live keep
     * parsing instead of failing wholesale on the device. Unknown flags that
     * aren't retired (typos) still fail the enum.
     */
    enabledFeatures: z.preprocess(
      (v) => (Array.isArray(v) ? v.filter((f) => !RETIRED_FEATURE_FLAGS.has(f as string)) : v),
      z.array(FeatureFlag),
    ),
    device: DeviceExtensions.optional(),
    telemetry: TelemetryConfig.optional(),
    remoteConfig: RemoteConfig.optional(),
    signage: Signage.optional(),
    meta: Meta,
  })
  .superRefine((cfg, ctx) => {
    const ids = new Set(cfg.profiles.map((p) => p.id));
    if (!ids.has(cfg.defaultProfileId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'defaultProfileId does not reference any profile in profiles[]',
        path: ['defaultProfileId'],
      });
    }
    cfg.schedules.forEach((s, i) => {
      if (!ids.has(s.profileId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'profileId does not reference any profile in profiles[]',
          path: ['schedules', i, 'profileId'],
        });
      }
    });
  });
export type MobiLauncherConfig = z.infer<typeof MobiLauncherConfig>;

// Barrel re-exports.
export * from './primitives.js';
export * from './features.js';
export * from './tiles.js';
export * from './layout.js';
export * from './profile.js';
export * from './admin.js';
export * from './device.js';
export * from './telemetry.js';
export * from './remote.js';
export * from './signage.js';
export * from './meta.js';
export * from './mdm.js';
